/** Descriptive landing page for frontier models without live access. */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

/** Interlinked business platform — where gated models are accessed after invite. */
const PLATFORM_APP_URL =
  process.env.REACT_APP_PLATFORM_APP_URL || 'https://qa.app.interlynked.com';

/**
 * Explains what a gated model decides, how it works, and its current maturity.
 *
 * @param {object} props
 * @param {object|null} props.model - Entry from FRONTIER_MODELS.
 * @param {() => void} props.onBack - Navigate back to chat.
 * @param {(model: object) => false|{ message: string }|void} [props.onRequestInvite] - Optional invite handler.
 */
export default function ModelDescriptionPage({ model, onBack, onRequestInvite }) {
  const [inviteMessage, setInviteMessage] = useState(null);

  // Reset invite alert and scroll when switching between model pages.
  useEffect(() => {
    setInviteMessage(null);
    window.scrollTo(0, 0);
  }, [model?.id]);

  if (!model) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', py: 4, px: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Model not found
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mt: 2, textTransform: 'none' }}>
          Back to chat
        </Button>
      </Box>
    );
  }

  /**
   * Opens the main Interlinked app for invite / access.
   * Return `false` from onRequestInvite to suppress navigation; `{ message }` to show an alert instead.
   */
  const handleInvite = () => {
    const result = onRequestInvite?.(model);
    if (result === false) return;
    if (result?.message) {
      setInviteMessage(result.message);
      return;
    }
    window.open(PLATFORM_APP_URL, '_blank', 'noopener,noreferrer');
  };

  /** Renders one or more paragraphs under a section heading. */
  const renderTextSection = (title, content) => {
    if (!content) return null;
    const paragraphs = Array.isArray(content) ? content : [content];
    return (
      <Box component="section">
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          {title}
        </Typography>
        <Stack spacing={1.5}>
          {paragraphs.map((paragraph) => (
            <Typography
              key={paragraph}
              color="text.secondary"
              sx={{ lineHeight: 1.75, maxWidth: '72ch' }}
            >
              {paragraph}
            </Typography>
          ))}
        </Stack>
      </Box>
    );
  };

  /** Renders model pipeline steps or caveats as a readable bulleted list. */
  const renderListSection = (title, items) => {
    if (!items?.length) return null;
    return (
      <Box component="section">
        <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
          {title}
        </Typography>
        <List disablePadding>
          {items.map((item) => (
            <ListItem key={item} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  mt: 1.1,
                  mr: 1.5,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  flexShrink: 0,
                }}
              />
              <ListItemText
                primary={item}
                primaryTypographyProps={{ color: 'text.secondary', lineHeight: 1.65 }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        maxWidth: 820,
        mx: 'auto',
        width: '100%',
        py: { xs: 3, md: 5 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 3, textTransform: 'none', color: 'text.secondary', px: 0 }}
      >
        Back to chat
      </Button>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
        {model.family && <Chip label={model.family} variant="outlined" size="small" />}
        {model.status && (
          <Chip label={model.status} color={model.statusColor || 'default'} size="small" />
        )}
      </Stack>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: 'text.primary',
          mb: 2,
        }}
      >
        {model.title}
      </Typography>

      <Stack spacing={1.5} sx={{ mb: 4, maxWidth: '72ch' }}>
        {(Array.isArray(model.summary) ? model.summary : [model.summary]).map((paragraph) => (
          <Typography
            key={paragraph}
            variant="h6"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            {paragraph}
          </Typography>
        ))}
      </Stack>

      <Divider sx={{ mb: 4 }} />

      <Stack spacing={4}>
        {renderTextSection('What it decides', model.decides)}
        {renderTextSection('What it returns', model.output)}
        {renderListSection('How it works', model.howItWorks)}
        {renderListSection('Status and considerations', model.notes)}
      </Stack>

      {inviteMessage && (
        <Alert severity="info" sx={{ mt: 5, mb: 3 }} onClose={() => setInviteMessage(null)}>
          {inviteMessage}
        </Alert>
      )}

      {model.invitable ? (
        <>
          <Divider sx={{ mt: 5, mb: 3 }} />
          <Button
            variant="contained"
            size="large"
            onClick={handleInvite}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              borderRadius: 2,
            }}
          >
            Get Invited to our main app
          </Button>
        </>
      ) : !model.hasLiveAccess ? (
        <>
          <Divider sx={{ mt: 5, mb: 3 }} />
          <Alert severity="info" icon={false}>
            This model is still in development and not yet available for access. Check back soon.
          </Alert>
        </>
      ) : null}
    </Box>
  );
}
